// src/utils/operatingAreas.js
//
// Pass 82E — MULTI-AREA OPERATIONS. ONE organization may operate MULTIPLE geographic teams / OPERATING AREAS without
// creating new organizations or duplicating people (SAS: Niseko + Furano inside `sas-niseko`). This pure module defines
// the identity + resolution contract. Storage follows the repo's existing convention: FLAT org-scoped top-level
// collections (`operatingAreas` / `serviceLocations` / `meetingPoints`), doc ids org-namespaced to avoid cross-tenant
// collision (`<organizationId>::<key>`), exactly like `seed_owner_john::motus-pilot-2`.
//
// STRICT SEPARATION (durable invariants — never conflate):
//   organization ≠ operating area ≠ service location ≠ meeting point;
//   operating area is NOT member category, NOT source relationship, NOT permission role;
//   one canonical human = one occupancy identity ACROSS areas (area filters visibility/pools, never availability).
// PURE — no Firestore, no React.

export const AREA_STATUS = { ACTIVE: 'active', PLANNED: 'planned', INACTIVE: 'inactive' };
export const LOCATION_STATUS = { ACTIVE: 'active', INACTIVE: 'inactive' };
export const UNRESOLVED_AREA = ''; // an empty operatingAreaId means "needs area / unresolved" — surfaced, never hidden.

const str = (v) => String(v == null ? '' : v).trim();

// Pass 82E.1 — HIERARCHICAL org-namespaced doc ids so identity reflects the actual ownership hierarchy and CANNOT
// collide across areas/locations inside one org (GAP B). A `furano` in another tenant never collides with SAS Furano,
// and a "Base" location in Niseko never collides with a "Base" location in Furano:
//   operating area:   <org>::<areaKey>                              e.g. sas-niseko::niseko
//   service location: <org>::<areaKey>::<locKey>                    e.g. sas-niseko::niseko::hirafu
//   meeting point:    <org>::<areaKey>::<locKeyOrAreaWide>::<mpKey> e.g. sas-niseko::niseko::hirafu::hotel-lobby
// Area-wide meeting points (§9) use the explicit AREA_WIDE_LOCATION_KEY sentinel in the location slot.
export const AREA_WIDE_LOCATION_KEY = '__area__';

export function operatingAreaDocId(organizationId, areaKey) {
  const o = str(organizationId); const k = str(areaKey);
  return o && k ? `${o}::${k}` : '';
}
export function serviceLocationDocId(organizationId, areaKey, locKey) {
  const o = str(organizationId); const a = str(areaKey); const l = str(locKey);
  return o && a && l ? `${o}::${a}::${l}` : '';
}
export function meetingPointDocId(organizationId, areaKey, locKeyOrAreaWide, mpKey) {
  const o = str(organizationId); const a = str(areaKey); const loc = str(locKeyOrAreaWide) || AREA_WIDE_LOCATION_KEY; const m = str(mpKey);
  return o && a && m ? `${o}::${a}::${loc}::${m}` : '';
}
// Segments of a namespaced id AFTER the organization prefix. `<org>::niseko::hirafu` → ['niseko','hirafu'].
export function keySegmentsOf(id) {
  const s = str(id);
  const i = s.indexOf('::');
  return i >= 0 ? s.slice(i + 2).split('::') : (s ? [s] : []);
}
// The AREA key (first segment after org) of any area/location/meeting-point id (or a bare area key).
export function areaKeyOf(id) { return keySegmentsOf(id)[0] || ''; }
// The LEAF key (last segment) — the location key of a serviceLocationId, or the point key of a meetingPointId.
export function leafKeyOf(id) { const seg = keySegmentsOf(id); return seg[seg.length - 1] || ''; }
// Does `childId` sit under `parentId` in the hierarchy (same org + same leading segments)?
export function isDescendantId(parentId, childId) {
  const p = str(parentId); const c = str(childId);
  return !!p && !!c && (c === p || c.startsWith(`${p}::`));
}

// ── Resolution: which operating area does a booking belong to? (§11) ─────────────────────────────────────────────────
// Precedence is EVIDENCE-BASED and config-driven (NO `if organizationId === 'sas-niseko'`): explicit booking value wins,
// then the authoritative SOURCE-config mapping for a source-backed booking, then a BOUNDED legacy org default, else
// unresolved. NEVER infers from guest names or free-text pickup.
export const AREA_VIA = { EXPLICIT: 'explicit', SOURCE_CONFIG: 'source_config', LEGACY_DEFAULT: 'legacy_default', UNRESOLVED: 'unresolved' };

// `ctx`: { defaultOperatingAreaId?, sourceAreaFor?(booking)->areaId|'' }. The legacy default applies ONLY to a booking
// that carries NO explicit operatingAreaId AND is NOT source-area-mapped — i.e. a genuine pre-multi-area record. Because
// every NEW native/case write stamps an explicit area (see requiresExplicitArea) and every source booking resolves via
// source config, this legacy path can only ever match a pre-82E record — it can never silently catch a NEW ambiguous one.
export function resolveBookingOperatingArea(booking = {}, ctx = {}) {
  const explicit = str(booking.operatingAreaId);
  if (explicit) return { operatingAreaId: explicit, via: AREA_VIA.EXPLICIT };

  const sourceArea = typeof ctx.sourceAreaFor === 'function' ? str(ctx.sourceAreaFor(booking)) : '';
  if (sourceArea) return { operatingAreaId: sourceArea, via: AREA_VIA.SOURCE_CONFIG };

  const legacyDefault = str(ctx.defaultOperatingAreaId);
  if (legacyDefault) return { operatingAreaId: legacyDefault, via: AREA_VIA.LEGACY_DEFAULT };

  return { operatingAreaId: UNRESOLVED_AREA, via: AREA_VIA.UNRESOLVED };
}

// Does a booking match a selected operations-area filter? `selected` = '' / 'all' → everything; 'unresolved' → only
// records that resolve to no area; otherwise the resolved area must equal the selection. Uses the SAME resolver so the
// filter agrees with every other read.
export const AREA_FILTER_ALL = 'all';
export const AREA_FILTER_UNRESOLVED = 'unresolved';
export function bookingMatchesAreaFilter(booking = {}, selected = AREA_FILTER_ALL, ctx = {}) {
  const sel = str(selected);
  if (!sel || sel === AREA_FILTER_ALL) return true;
  const { operatingAreaId, via } = resolveBookingOperatingArea(booking, ctx);
  if (sel === AREA_FILTER_UNRESOLVED) return via === AREA_VIA.UNRESOLVED;
  return operatingAreaId === sel;
}

// ── Multi-area cutover contract (§13/§16/§36) ────────────────────────────────────────────────────────────────────────
// A NEW Motus-native booking / booking case MUST carry an explicit operatingAreaId (never a silent Niseko default). The
// write path calls this to fail closed. Source materialization is exempt (its area comes from authoritative source
// config); a booking that is source-backed is not held to the explicit-native requirement here.
export function requiresExplicitArea({ isSourceBacked = false } = {}) {
  return !isSourceBacked;
}
export function assertExplicitAreaForNewRecord(operatingAreaId, { isSourceBacked = false } = {}) {
  if (!requiresExplicitArea({ isSourceBacked })) return true;
  if (!str(operatingAreaId)) {
    const err = new Error('An operating area is required for a new booking.');
    err.code = 'MISSING_OPERATING_AREA';
    throw err;
  }
  return true;
}

// ── Structured-vs-source-text (§15/§42) ──────────────────────────────────────────────────────────────────────────────
// A source booking's free-text pickup (`locationLabel`) is NEVER destroyed or fuzzy-matched. A structured meetingPointId
// is only ever attached by explicit operator-confirmed configuration. This helper reports the display truth: prefer a
// resolved meeting-point name when present, else the preserved raw label.
export function displayMeetingLabel(booking = {}, { meetingPointName = '' } = {}) {
  return str(meetingPointName) || str(booking.locationLabel) || '';
}

// ── Pass 83A — staff ↔ area assignment eligibility (§43/§44/§45) ─────────────────────────────────────────────────────
// A season membership's operating areas (canonical ids). Empty during the transition before a member is roster-assigned.
export function memberOperatingAreaIds(member = {}, seasonId = '') {
  const sm = (member.seasonMemberships && seasonId && member.seasonMemberships[seasonId]) || {};
  const ids = sm.operatingAreaIds;
  return Array.isArray(ids) ? ids.map((v) => String(v || '')).filter(Boolean) : [];
}

// Does this member serve a given operating area for the season? A member with NO area assignment yet is treated as
// unrestricted (transition-safe — §55: current staff resolve via the default until roster assignment), so we never
// hide real staff before 83A assignment; once assigned, a Furano booking excludes a Niseko-only instructor.
export function memberServesArea(member = {}, operatingAreaId = '', seasonId = '') {
  if (!operatingAreaId) return true;
  const ids = memberOperatingAreaIds(member, seasonId);
  if (ids.length === 0) return true;
  return ids.includes(operatingAreaId);
}

// A member usable as a REAL production assignment candidate — a TEST account is NEVER real supply (§9/§45), unless an
// explicit test/debug context opts it in.
export function isProductionAssignable(member = {}, { includeTest = false } = {}) {
  if (member.testAccount === true && !includeTest) return false;
  return true;
}

// Filter an activity-eligible candidate list to a booking's operating area + exclude TEST from real supply (§43/§45).
export function filterAreaAssignmentCandidates(candidates = [], { operatingAreaId = '', seasonId = '', includeTest = false } = {}) {
  return candidates.filter((m) => isProductionAssignable(m, { includeTest }) && memberServesArea(m, operatingAreaId, seasonId));
}

// Normalize the three structured-location ids onto a record (empty-safe; never invents identity).
export function structuredLocationFields(rec = {}) {
  return {
    operatingAreaId: str(rec.operatingAreaId),
    serviceLocationId: str(rec.serviceLocationId),
    meetingPointId: str(rec.meetingPointId),
  };
}
